<template>
  <div>
    <SubscriptionModal
      :show="showSubscriptionModal"
      :current-plan-name="planName"
      :plans="plans"
      :loading="isUpdatingSubscription"
      @close="closeSubscriptionModal"
      @input="handleSubscriptionChange"
    />

    <button :class="buttonClass" @click="openSubscriptionModal">
      {{ translationButtonCtaLabel }}
    </button>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SubscriptionModal from '@/components/detailedRentalObject/SubscriptionModal.vue';
import subscription from '@/mixins/tenancy/subscription';
import cpi from '@/mixins/tenancy/cpi';
import i18nCommon from '@modules/i18n/mixins/Common';
import { TenancyApi } from '@modules/husleieapi2';

export default {
  components: {
    SubscriptionModal,
  },
  mixins: [i18nCommon, subscription, cpi],
  props: {
    landlordId: [String, Number],
    tenancyId: [String, Number],
    origin: [String],
    buttonCTA: [String],
  },
  data() {
    return {
      showSubscriptionModal: false,
      plans: [],
      isUpdatingSubscription: false,
    };
  },
  computed: {
    ...mapGetters('tenancy', ['getTenancy']),
    ...mapGetters(['getLandlordProfileMeta']),

    buttonClass() {
      const baseClasses = ['btn'];
      return baseClasses.concat(this.isUpgradeable ? ['btn-primary'] : ['btn-outline-secondary']);
    },
    currentSubscriptionPlanName() {
      if (!this.getTenancy.subscription.subscriptionPlanName) {
        return 'free';
      }
      return this.getTenancy.subscription.subscriptionPlanName.toLowerCase();
    },
    isProfessionalLandlord() {
      const landlord = this.getLandlordProfileMeta(this.landlordId);

      return landlord && landlord.meta.isProfessional;
    },
    translationButtonCtaLabel() {
      if (this.buttonCTA) {
        return this.buttonCTA;
      }
      if (['free', 'basic'].includes(this.currentSubscriptionPlanName)) {
        return this.translate('updateSubscription.button.upgrade');
      }
      if (this.currentSubscriptionPlanName === 'premium') {
        return this.translate('updateSubscription.button.downgrade');
      }
    },
    translationErrorCouldNotLoadPlans() {
      return this.translate('error.could not load plans');
    },
  },

  methods: {
    ...mapActions('tenancy', ['fetchTenancy']),

    async updateTenancyStore() {
      try {
        this.fetchTenancy({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
        });
      } catch (error) {
        throw new Error('Failed to fetch tenancy', error);
      }
    },
    openSubscriptionModal() {
      this.showSubscriptionModal = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'openUpsellModal',
        virtualPageURL: window.location.pathname,
        buttonText: this.buttonCTA,
        currentPlan: this.planName,
      });
    },
    closeSubscriptionModal() {
      this.showSubscriptionModal = false;
    },
    updateInterfaceWithNewPlan(message, mode) {
      this.showSubscriptionModal = false;
      this.$toast(message, mode);
    },
    async fetchAvailablePlans() {
      try {
        const api = new TenancyApi();
        this.plans = await api.getLandlordTenancyAvailableSubscriptions(this.landlordId, this.tenancyId);
      } catch (error) {
        this.$toast(this.translationErrorCouldNotLoadPlans, 'error');
      }
    },
    async handleSubscriptionChange(newPlanName) {
      this.isUpdatingSubscription = true;

      try {
        await this.changeSubscriptionPlan(
          this.landlordId,
          this.tenancyId,
          this.plans,
          this.planName,
          newPlanName,
          'updateSubscriptionButton'
        );
        this.updateInterfaceWithNewPlan(this.translationAlertChangedTo(newPlanName), 'success');

        if (!this.isProfessionalLandlord && newPlanName === 'Basic' && this.planName === 'Premium') {
          this.handleCPIRemoval(this.landlordId, this.tenancyId, this.getTenancy.dueDay, this.getTenancy.startDate);
        }

        await this.updateTenancyStore();
      } catch {
        this.updateInterfaceWithNewPlan(this.translationAlertChangeFailed(newPlanName), 'error');
      }

      this.isUpdatingSubscription = false;
    },
    translationAlertChangedTo(planName) {
      return this.translate('subscription.alert.changed subscription to {planName}', { planName });
    },
    translationAlertChangeFailed(planName) {
      return this.translate('subscription.alert.change of subscription to {planName} failed', { planName });
    },
  },
  async mounted() {
    await this.fetchAvailablePlans();
  },
};
</script>
